'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { startersFor, type StarterPrompt } from '@/lib/ai/page-context'
import type { ChatContext } from '@/lib/ai/types'

const ICONS: Record<StarterPrompt['icon'], React.ElementType> = {
  barChart: Icons.barChart,
  fileText: Icons.fileText,
  search: Icons.search,
  checkCircle: Icons.checkCircle,
  xCircle: Icons.xCircle,
  send: Icons.send,
  mail: Icons.mail,
  download: Icons.download,
  brain: Icons.brain,
  bot: Icons.bot,
  database: Icons.database,
  lightbulb: Icons.lightbulb,
  helpCircle: Icons.helpCircle,
  info: Icons.info,
  activity: Icons.activity,
  flag: Icons.flag,
}

interface CapabilityBubblesProps {
  onSelect: (query: string) => void
  /** Page context — picks the per-page starter set. Defaults to the generic set. */
  context?: ChatContext
}

/** Page-aware starter prompts shown in the empty state of the AI Manager. */
export function CapabilityBubbles({ onSelect, context }: CapabilityBubblesProps) {
  const starters = startersFor(context ?? { page: '/', invoice_id: null, queue: null, tab: null, highlight: null, selection: null })
  return (
    <div className="flex flex-wrap justify-center gap-2 p-2">
      {starters.map((cap, i) => {
        const Icon = ICONS[cap.icon] ?? Icons.sparkles
        return (
          <motion.button
            key={`${cap.label}-${i}`}
            type="button"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25, ease: 'easeOut' }}
            onClick={() => onSelect(cap.query)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-full',
              'bg-white border border-brand-cornflower/20',
              'text-sm text-brand-navy',
              'shadow-sm',
              'transition-all duration-200',
              'hover:bg-brand-cornflower/10 hover:border-brand-cornflower/40 hover:shadow-md',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
            )}
          >
            <Icon className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
            <span className="text-xs sm:text-sm font-medium">{cap.label}</span>
          </motion.button>
        )
      })}
    </div>
  )
}
